import React, { useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { SortableTableHeader, Text } from '../ui'
import HistoricoContatoDetalheModal from './HistoricoContatoDetalheModal'
import {
  feedbackLabel,
  formatarDataHoraContato,
  MeioContatoBadge,
  resolveContatoAtendido,
  TeveContatoBadge,
  TipoContatoBadge,
  type HistoricoContatosTableRow,
} from './historicoContatosShared'
import type { ContatoCorretorGrupo, LigacaoRelatorioRow } from '../../types/comercialContatosPortal'

export type ContatosPortalSortKey =
  | 'comercial_responsavel'
  | 'consultant_responsavel'
  | 'corretor'
  | 'data_horario'
  | 'meio'
  | 'tipo'
  | 'sucesso'
  | 'motivo'

type HistoricoContatosAgrupadosTableProps = {
  grupos: ContatoCorretorGrupo[]
  sortKey: ContatosPortalSortKey
  sortOrder: 'asc' | 'desc'
  onSort: (key: string) => void
}

const thClass =
  'px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'
const tdClass = 'px-4 py-4 text-left align-top'
const rowFocusClass =
  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset'

/** Colunas da tabela (chevron + 8 campos). */
const COL_COUNT = 9
const expandedFrameColor = 'border-gray-400 dark:border-gray-500'

/**
 * Borda contínua em volta do histórico expandido (cabeçalho + contatos),
 * no mesmo espírito do painel expandido do dashboard SCGP.
 */
function expandedFrameClass(
  colStart: number,
  colSpan: number,
  edges: { top?: boolean; bottom?: boolean }
): string {
  const classes = [expandedFrameColor]
  if (colStart === 0) classes.push('border-l-2')
  if (colStart + colSpan >= COL_COUNT) classes.push('border-r-2')
  if (edges.top) classes.push('border-t-2')
  if (edges.bottom) classes.push('border-b-2')
  return classes.join(' ')
}

function labelResumoOrigem(origem: ContatoCorretorGrupo['resumoOrigem']): string {
  return origem === 'ultimo_sucesso'
    ? 'Último com sucesso'
    : 'Último contato (sem sucesso)'
}

/**
 * Tabela de contatos agrupados por corretora (padrão Contatos Portal / Diário).
 * Linha-pai: resumo. Clique expande o histórico e recolhe os demais.
 * Linhas-filhas: contatos da corretora; clique abre o modal.
 */
const sortHeaderClass = 'px-4 py-3'

const HistoricoContatosAgrupadosTable: React.FC<HistoricoContatosAgrupadosTableProps> = ({
  grupos,
  sortKey,
  sortOrder,
  onSort,
}) => {
  const [expandedCorretorId, setExpandedCorretorId] = useState<string | null>(null)
  const [selectedRow, setSelectedRow] = useState<HistoricoContatosTableRow | null>(null)

  const toggleExpand = (corretorId: string) => {
    setExpandedCorretorId(prev => (prev === corretorId ? null : corretorId))
  }

  return (
    <>
      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
          <tr>
            <th className="w-10 px-2 py-3" aria-hidden />
            <SortableTableHeader
              sortKey="comercial_responsavel"
              currentSort={sortKey}
              sortOrder={sortOrder}
              onSort={onSort}
              compact
              className={sortHeaderClass}
            >
              Comercial responsável
            </SortableTableHeader>
            <SortableTableHeader
              sortKey="consultant_responsavel"
              currentSort={sortKey}
              sortOrder={sortOrder}
              onSort={onSort}
              compact
              className={sortHeaderClass}
            >
              Consultor responsável
            </SortableTableHeader>
            <SortableTableHeader
              sortKey="corretor"
              currentSort={sortKey}
              sortOrder={sortOrder}
              onSort={onSort}
              compact
              className={sortHeaderClass}
            >
              Corretor
            </SortableTableHeader>
            <SortableTableHeader
              sortKey="data_horario"
              currentSort={sortKey}
              sortOrder={sortOrder}
              onSort={onSort}
              compact
              className={sortHeaderClass}
            >
              Data e Horário
            </SortableTableHeader>
            <SortableTableHeader
              sortKey="meio"
              currentSort={sortKey}
              sortOrder={sortOrder}
              onSort={onSort}
              compact
              className={sortHeaderClass}
            >
              Meio de Contato
            </SortableTableHeader>
            <SortableTableHeader
              sortKey="tipo"
              currentSort={sortKey}
              sortOrder={sortOrder}
              onSort={onSort}
              compact
              className={sortHeaderClass}
            >
              Tipo de Contato
            </SortableTableHeader>
            <SortableTableHeader
              sortKey="sucesso"
              currentSort={sortKey}
              sortOrder={sortOrder}
              onSort={onSort}
              compact
              className={sortHeaderClass}
            >
              Sucesso no contato
            </SortableTableHeader>
            <SortableTableHeader
              sortKey="motivo"
              currentSort={sortKey}
              sortOrder={sortOrder}
              onSort={onSort}
              compact
              className={sortHeaderClass}
            >
              Motivo
            </SortableTableHeader>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {grupos.map(grupo => {
            const isExpanded = expandedCorretorId === grupo.corretor_id
            const { resumo } = grupo
            const qtd = grupo.contatos.length
            const dicaResumo = labelResumoOrigem(grupo.resumoOrigem)

            const detailIsEmpty = grupo.contatos.length === 0
            const frame = (colStart: number, colSpan = 1, edges?: { top?: boolean; bottom?: boolean }) =>
              expandedFrameClass(colStart, colSpan, edges ?? {})

            return (
              <React.Fragment key={grupo.corretor_id}>
                <tr
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpand(grupo.corretor_id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      toggleExpand(grupo.corretor_id)
                    }
                  }}
                  className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${rowFocusClass}`}
                >
                  <td className="px-2 py-4 text-gray-500 dark:text-gray-400 align-top">
                    {isExpanded ? (
                      <ChevronDownIcon className="h-4 w-4" aria-hidden />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" aria-hidden />
                    )}
                  </td>
                  <td className={tdClass}>
                    <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                      {resumo.comercial_responsavel_nome || '—'}
                    </Text>
                  </td>
                  <td className={tdClass}>
                    <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                      {resumo.consultant_responsavel_nome || '—'}
                    </Text>
                  </td>
                  <td className={tdClass}>
                    <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                      {grupo.corretora_nome || '—'}
                    </Text>
                    <Text size="xs" color="muted" className="mt-0.5 block">
                      {qtd} {qtd === 1 ? 'contato' : 'contatos'}
                    </Text>
                  </td>
                  <td className={`${tdClass} whitespace-nowrap`}>
                    <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                      {formatarDataHoraContato(resumo.data, resumo.horario)}
                    </Text>
                    <Text size="xs" color="muted" className="mt-0.5 block">
                      {dicaResumo}
                    </Text>
                  </td>
                  <td className={tdClass}>
                    <MeioContatoBadge tipo={resumo.tipo_contato} />
                  </td>
                  <td className={tdClass}>
                    <TipoContatoBadge tipo={resumo.tipo} />
                  </td>
                  <td className={tdClass}>
                    <TeveContatoBadge teveContato={resumo.teve_contato} />
                  </td>
                  <td className={tdClass}>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300">
                      {resumo.motivo || '-'}
                    </Text>
                  </td>
                </tr>

                {isExpanded && (
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <td className={`w-10 px-2 py-3 ${frame(0, 1, { top: true, bottom: detailIsEmpty })}`} aria-hidden />
                    <td className={`${thClass} ${frame(1, 3, { top: true, bottom: detailIsEmpty })}`} colSpan={3}>
                      <div className="grid grid-cols-3 gap-4">
                        <span>Responsável</span>
                        <span>Pessoa contactada</span>
                        <span>Feedback geral</span>
                      </div>
                    </td>
                    <td className={`${thClass} ${frame(4, 1, { top: true, bottom: detailIsEmpty })}`}>Data e Horário</td>
                    <td className={`${thClass} ${frame(5, 1, { top: true, bottom: detailIsEmpty })}`}>Meio de Contato</td>
                    <td className={`${thClass} ${frame(6, 1, { top: true, bottom: detailIsEmpty })}`}>Tipo de Contato</td>
                    <td className={`${thClass} ${frame(7, 1, { top: true, bottom: detailIsEmpty })}`}>Sucesso no contato</td>
                    <td className={`${thClass} ${frame(8, 1, { top: true, bottom: detailIsEmpty })}`}>Motivo</td>
                  </tr>
                )}

                {isExpanded &&
                  grupo.contatos.map((contato, index) => {
                    const isLast = index === grupo.contatos.length - 1
                    return (
                      <tr
                        key={contato.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedRow(contato)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setSelectedRow(contato)
                          }
                        }}
                        className={`cursor-pointer ${rowFocusClass} ${
                          contato.teve_contato
                            ? 'bg-green-100 hover:bg-green-200/80 dark:bg-green-900/30 dark:hover:bg-green-900/45'
                            : 'bg-red-100 hover:bg-red-200/80 dark:bg-red-900/30 dark:hover:bg-red-900/45'
                        }`}
                      >
                        <td className={`px-2 py-4 ${frame(0, 1, { bottom: isLast })}`} aria-hidden />
                        <td className={`${tdClass} ${frame(1, 3, { bottom: isLast })}`} colSpan={3}>
                          <ContatoFilhoDetalhes row={contato} />
                        </td>
                        <ContatoFilhoMetaCells row={contato} bottom={isLast} />
                      </tr>
                    )
                  })}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>

      <HistoricoContatoDetalheModal
        row={selectedRow}
        showCorretor
        onClose={() => setSelectedRow(null)}
      />
    </>
  )
}

function ContatoFilhoDetalhes({ row }: { row: LigacaoRelatorioRow }) {
  const contatoAtendido = resolveContatoAtendido(row)

  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="min-w-0">
        <Text size="sm" className="text-gray-900 dark:text-white font-medium break-words">
          {row.registered_by_name?.trim() || '-'}
        </Text>
      </div>
      <div className="min-w-0">
        {contatoAtendido ? (
          <div>
            <Text size="sm" className="text-gray-900 dark:text-white font-medium break-words">
              {contatoAtendido.nome}
            </Text>
            {contatoAtendido.cargo ? (
              <Text size="xs" color="muted">
                {contatoAtendido.cargo}
              </Text>
            ) : null}
          </div>
        ) : (
          <Text size="sm" color="muted">
            -
          </Text>
        )}
      </div>
      <div className="min-w-0">
        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
          {feedbackLabel(row.feedback_geral)}
        </Text>
      </div>
    </div>
  )
}

function ContatoFilhoMetaCells({ row, bottom }: { row: LigacaoRelatorioRow; bottom: boolean }) {
  const cell = (colStart: number) => expandedFrameClass(colStart, 1, { bottom })

  return (
    <>
      <td className={`${tdClass} whitespace-nowrap ${cell(4)}`}>
        <Text size="sm" className="text-gray-500 dark:text-gray-400">
          {formatarDataHoraContato(row.data, row.horario)}
        </Text>
      </td>
      <td className={`${tdClass} ${cell(5)}`}>
        <MeioContatoBadge tipo={row.tipo_contato} />
      </td>
      <td className={`${tdClass} ${cell(6)}`}>
        <TipoContatoBadge tipo={row.tipo} />
      </td>
      <td className={`${tdClass} ${cell(7)}`}>
        <TeveContatoBadge teveContato={row.teve_contato} plain />
      </td>
      <td className={`${tdClass} ${cell(8)}`}>
        <Text size="sm" className="text-gray-700 dark:text-gray-300">
          {row.motivo || '-'}
        </Text>
      </td>
    </>
  )
}

export default HistoricoContatosAgrupadosTable
